import { Injectable } from '@angular/core';
import { extractDepartmentId } from '@features/shared/departments/domain/utils/extract-department-id.util';
import { Patient } from '../../domain/models/patient.model';
import { toPatientClinicalStatus } from '../../domain/models/patient-clinical-status.model';
import { PatientUpdateInput } from '../../domain/models/patient-update-input.model';
import { PatientTutorRef } from '../../domain/models/patient-tutor-ref.model';
import { PatientDto } from '../dtos/patient.dto';
import { PatientMetricsDto } from '../dtos/patient-metrics.dto';
import { PatientMetrics } from '../../domain/models/patient-metrics.model';
import { ClientRefDto } from '../dtos/client-ref.dto';

@Injectable()
export class PatientMapper {
  public toPatient(dto: PatientDto): Patient {
    return {
      id: dto.ExternalId,
      name: dto.Name,
      speciesId: dto.SpeciesId ?? null,
      breedId: dto.BreedId ?? null,
      genderId: dto.GenderId ?? null,
      // Backend only ever sends 1 (Macho), 2 (Hembra) or null (Desconocido) -- narrower than the
      // DTO's `number | null` (a plain passthrough type shared by every numeric field on the DTO).
      gender: (dto.Gender ?? null) as 1 | 2 | null,
      neutered: dto.Neutered ?? null,
      bloodGroup: dto.BloodGroup ?? null,
      bloodGroupId: dto.BloodGroupId ?? null,
      microchip: dto.Microchip ?? null,
      microchipDate: dto.MicrochipDate ?? null,
      criticalNotes: dto.CriticalNotes ?? null,
      remarks: dto.Remarks ?? null,
      insurance: dto.Insurance ?? null,
      insuranceCompanyId: dto.InsuranceCompanyId ?? null,
      lastConsultation: dto.LastConsultation ?? null,
      weight: dto.Weight ?? null,
      dateOfBirth: dto.DateOfBirth ?? null,
      archived: dto.Archived,
      deceased: dto.Deceased,
      deceasedDate: dto.DeceasedDate ?? null,
      tutorId: dto.ExternalClientId,
      // Resolved later by the store via a batched clients lookup -- the mapper only knows
      // about the raw PatientDto, which has no tutor name field.
      tutorName: null,
      pictureThumb: dto.PictureThumb ?? null,
      pictureMedium: dto.PictureMedium ?? null,
      tags: (dto.Tags ?? []).filter((tag): tag is string => !!tag),
      clinicalStatus: toPatientClinicalStatus(dto.ClinicalStatus),
      emergencyVisit: dto.EmergencyVisit ?? false,
      admittedAt: dto.AdmittedAt ?? null,
      homeDepartmentExternalId: extractDepartmentId(dto.HomeDepartmentUrl),
    };
  }

  // POST/PUT/PATCH /api/patients now hit typed `CreatePatientRequest`/`UpdatePatientRequest`
  // endpoints (PascalCase, `PropertyNamingPolicy = null` -- see Program.cs) -- plain numeric
  // ids are sent as-is; the backend itself stringifies them into ProVet's own free-text
  // species/breed/gender fields on write (see CreatePatientRequestProviderPayloadMapper).
  public toCreatePayload(input: PatientUpdateInput): Record<string, unknown> {
    return this.toPayload(input);
  }

  public toUpdatePayload(changes: PatientUpdateInput): Record<string, unknown> {
    return this.toPayload(changes);
  }

  public toTutorRef(dto: ClientRefDto): PatientTutorRef {
    return { id: dto.ExternalId, fullName: this.resolveTutorFullName(dto) };
  }

  public toPatientMetrics(dto: PatientMetricsDto): PatientMetrics {
    return {
      newPatientsLast30Days: dto.NewPatientsLast30Days ?? 0,
      deceasedPatients: dto.DeceasedPatients ?? 0,
    };
  }

  private toPayload(input: PatientUpdateInput): Record<string, unknown> {
    const payload: Record<string, unknown> = {};

    if (input.name !== undefined) payload['Name'] = input.name;
    if (input.speciesId !== undefined) payload['SpeciesId'] = input.speciesId;
    if (input.breedId !== undefined) payload['BreedId'] = input.breedId;
    if (input.genderId !== undefined) payload['GenderId'] = input.genderId;
    // `weight` is deliberately NOT forwarded here -- Provet's Patient.weight is read-only
    // (auto-computed from the patient's most recent weight record), so including it in this
    // payload is a silent no-op. PatientDetailStore.save() reads `changes.weight` itself and
    // records it via the dedicated weight sub-resource instead (repository.recordWeight()).
    if (input.dateOfBirth !== undefined) payload['DateOfBirth'] = input.dateOfBirth;
    if (input.tutorId !== undefined) payload['TutorId'] = input.tutorId;
    if (input.bloodGroupId !== undefined) payload['BloodGroupId'] = input.bloodGroupId;
    if (input.microchip !== undefined) payload['Microchip'] = input.microchip;
    if (input.microchipDate !== undefined) payload['MicrochipDate'] = input.microchipDate;
    if (input.criticalNotes !== undefined) payload['CriticalNotes'] = input.criticalNotes;
    if (input.remarks !== undefined) payload['Remarks'] = input.remarks;
    if (input.insurance !== undefined) payload['Insurance'] = input.insurance;
    if (input.insuranceCompanyId !== undefined) payload['InsuranceCompanyId'] = input.insuranceCompanyId;
    if (input.deceased !== undefined) payload['Deceased'] = input.deceased;
    if (input.deceasedDate !== undefined) payload['DeceasedDate'] = input.deceasedDate;
    // A PRESENT array (including []) is a full tags replacement -- see `PatientUpdateInput.tags`.
    if (input.tags !== undefined) payload['Tags'] = input.tags;

    return payload;
  }

  private resolveTutorFullName(dto: ClientRefDto): string {
    const fullName = dto.FullName?.trim();
    if (fullName) return fullName;

    const combined = [dto.FirstName, dto.LastName].filter(Boolean).join(' ').trim();
    if (combined) return combined;

    return dto.Email ?? '';
  }
}
